import { Box, Button, Link, useMediaQuery, useTheme } from '@mui/material'
import { LinkButton } from '@e3dc-react/shell/Elements/Buttons'
import React from 'react'
import { makeStyles } from 'tss-react/mui'
import { useParams } from 'react-router-dom'
import { EditorSkeleton } from '@e3dc-react/shell/Elements/Skeletons'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { E3CreateMacro } from '@e3dc-react/icons'

import { Retrofit } from 'src/Libs/retrofit/models/retrofit.model'
import Container from './Container'
import { useRetrofitPartners } from 'src/Libs/retrofit/hooks'
import CompanyCard from './CompanyCard'
import { format, min } from '@e3dc-react/shell/Libs/date'
import { Typography } from '@e3dc-react/shell/Elements/Typography'
import useModals from 'src/Hooks/useModals'
import { checkIfRecommendationInvalid, checkIfRecommendationOutdated } from '../../utils'

const useStyles = makeStyles()(theme => ({
  headlineContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
  },
  headlineTitle: {
    color: theme.palette.primary.main,
  },
  headlineIcon: {
    color: theme.palette.primary.main,
  },
  headlineSubtitle: {
    span: {
      color: theme.shape.recomission.endDateColor,
    },
  },
  infoContainer: {
    marginTop: theme.spacing(2),
  },
  findPartnerContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
  },
  companyCardsContainer: {
    marginTop: theme.spacing(2),
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(2),
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
    },
  },
}))

interface InstallerContactProps {
  retrofitData: Retrofit
}

/**
 * Component to display installer contact info
 */
const InstallerContact: React.FC<InstallerContactProps> = ({ retrofitData }) => {
  const theme = useTheme()
  const downMd = useMediaQuery(theme.breakpoints.down('md'))
  const { t } = useCustomTranslation()
  const { installationId } = useParams()
  const { data, isLoading: attachedCompaniesLoading } = useRetrofitPartners(installationId)
  const { retrofitRecommendations, infinityOption } = retrofitData
  const { classes } = useStyles()
  const { openInformationModal } = useModals()

  const validRecommendations = retrofitRecommendations.filter(
    elem => !checkIfRecommendationInvalid(elem) && !checkIfRecommendationOutdated(elem) && elem.info !== 'infinity'
  )
  const expiresDate = validRecommendations.length ? min(validRecommendations?.map(elem => elem.expiresAt)) : undefined
  const infinityExpiresDate = retrofitData.infinityOption?.expiresAt

  const hasValidInfinityOption = infinityOption && !checkIfRecommendationInvalid(infinityOption) && !checkIfRecommendationOutdated(infinityOption)

  if (attachedCompaniesLoading) {
    return (
      <Container>
        <EditorSkeleton />
      </Container>
    )
  }

  return (
    <Container>
      <Box className={classes.headlineContainer}>
        <E3CreateMacro className={classes.headlineIcon} />
        <Box>
          <Typography variant={downMd ? 'h4' : 'h2'} className={classes.headlineTitle}>
            {'retrofit-check.available.headline'}
          </Typography>
          {validRecommendations && expiresDate && (
            <Typography
              variant="subtitle2"
              className={classes.headlineSubtitle}
              components={{
                date: <Box component={'span'} />,
                page: (
                  <Link
                    component={'button'}
                    onClick={() =>
                      openInformationModal({
                        title: t('retrofit-check.available.suggestions.infinity.details.title'),
                        textKey: 'retrofit-check.available.suggestions.infinity.details.text',
                      })
                    }
                  />
                ),
              }}
              values={{ expiresAt: format(expiresDate, t('common.format.date-fns-date')) }}
            >
              {retrofitData.infinityOption ? t('retrofit-check.available.subheadline-plus') : t('retrofit-check.available.subheadline-simple')}
            </Typography>
          )}
          {hasValidInfinityOption && infinityExpiresDate && (
            <Typography
              variant="subtitle2"
              className={classes.headlineSubtitle}
              components={{
                date: <Box component={'span'} />,
                page: (
                  <Link
                    component={'button'}
                    onClick={() =>
                      openInformationModal({
                        title: t('retrofit-check.available.suggestions.infinity.details.title'),
                        textKey: 'retrofit-check.available.suggestions.infinity.details.text',
                      })
                    }
                  />
                ),
              }}
              values={{ expiresAt: format(infinityExpiresDate, t('common.format.date-fns-date')) }}
            >
              {t('retrofit-check.available.subheadline-infinity-plus')}
            </Typography>
          )}
        </Box>
      </Box>
      <Box className={classes.infoContainer}>
        {data.length > 0 ? (
          <>
            <Typography variant="body2">{t('retrofit-check.available.companies-exist.text')}</Typography>
            <Box className={classes.companyCardsContainer}>
              {data.map(elem => (
                <CompanyCard data={elem} key={elem.name} />
              ))}
            </Box>
          </>
        ) : (
          <Box className={classes.findPartnerContainer}>
            <Typography variant="body2">{t('retrofit-check.available.no-companies.text')}</Typography>
            <LinkButton
              link={t('retrofit-check.available.no-companies.link')}
              content={<Button variant="contained"> {t('retrofit-check.available.no-companies.link-title')}</Button>}
            />
          </Box>
        )}
      </Box>
    </Container>
  )
}

export default InstallerContact
